import React, { useState, useEffect } from 'react';
import { ssoService } from '../../../services/ssoService';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Globe, CheckCircle, Clock, XCircle, Trash2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

const DomainVerification = ({ organizationId, ssoConfigId, onUpdate }) => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [autoAssignRole, setAutoAssignRole] = useState('member');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (organizationId) {
      loadDomains();
    }
  }, [organizationId]);

  const loadDomains = async () => {
    setLoading(true);
    try {
      const { data, error } = await ssoService?.getDomains(organizationId);
      if (!error && data) {
        setDomains(data);
      }
    } catch (error) {
      console.error('Error loading domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain?.trim()) {
      toast?.error('Please enter a domain name');
      return;
    }

    // Validate domain format
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    if (!domainRegex?.test(newDomain)) {
      toast?.error('Please enter a valid domain (e.g., example.com)');
      return;
    }

    setAdding(true);
    try {
      const { error } = await ssoService?.addDomain(
        ssoConfigId,
        organizationId,
        newDomain?.toLowerCase(),
        autoAssignRole
      );
      
      if (error) throw error;
      
      toast?.success('Domain added successfully');
      setNewDomain('');
      setShowAddDomain(false);
      loadDomains();
      onUpdate?.();
    } catch (error) {
      console.error('Error adding domain:', error);
      toast?.error(error?.message || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const handleVerifyDomain = async (domainId) => {
    try {
      const { error } = await ssoService?.verifyDomain(domainId);
      if (error) throw error;
      
      toast?.success('Domain verified successfully');
      loadDomains();
      onUpdate?.();
    } catch (error) {
      console.error('Error verifying domain:', error);
      toast?.error(error?.message || 'Failed to verify domain');
    }
  };

  const handleRemoveDomain = async (domainId) => {
    if (!confirm('Are you sure you want to remove this domain?')) return;

    try {
      const { error } = await ssoService?.removeDomain(domainId);
      if (error) throw error;
      
      toast?.success('Domain removed successfully');
      loadDomains();
      onUpdate?.();
    } catch (error) {
      console.error('Error removing domain:', error);
      toast?.error(error?.message || 'Failed to remove domain');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'pending':
        return 'Pending Verification';
      case 'failed':
        return 'Verification Failed';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Domain Verification</h2>
              <p className="text-sm text-muted-foreground">
                Register domains for automatic team assignment
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            onClick={() => setShowAddDomain(!showAddDomain)}
          >
            Add Domain
          </Button>
        </div>
      </div>

      <div className="p-6">
        {/* Add Domain Form */}
        {showAddDomain && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-foreground mb-4">Add New Domain</h3>
            <div className="space-y-4">
              <Input
                label="Domain Name"
                placeholder="example.com"
                value={newDomain}
                onChange={(e) => setNewDomain(e?.target?.value)}
                description="Enter the domain without protocol (e.g., example.com)"
              />
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Auto-assign Role
                </label>
                <select
                  value={autoAssignRole}
                  onChange={(e) => setAutoAssignRole(e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Users from this domain will automatically be assigned this role
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleAddDomain}
                  loading={adding}
                  disabled={adding || !newDomain?.trim()}
                  size="sm"
                >
                  Add Domain
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowAddDomain(false);
                    setNewDomain('');
                  }}
                  disabled={adding}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Domains List */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading domains...</p>
          </div>
        ) : domains?.length === 0 ? (
          <div className="text-center py-8">
            <Globe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-muted-foreground mb-2">No domains registered</p>
            <p className="text-sm text-muted-foreground">
              Add a domain to enable automatic team assignment for SSO users
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {domains?.map(domain => (
              <div
                key={domain?.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1">
                  {getStatusIcon(domain?.verification_status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{domain?.domain}</span>
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                        {domain?.auto_assign_role}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {getStatusText(domain?.verification_status)}
                      {domain?.verified_at && (
                        <span className="ml-2">
                          • Verified {new Date(domain?.verified_at)?.toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {domain?.verification_status === 'pending' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleVerifyDomain(domain?.id)}
                    >
                      Verify
                    </Button>
                  )}
                  <button
                    onClick={() => handleRemoveDomain(domain?.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove domain"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* DNS Verification Instructions */}
        {domains?.some(d => d?.verification_status === 'pending') && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">DNS Verification Required</h4>
            <p className="text-sm text-blue-800 mb-3">
              To verify domain ownership, add the following TXT record to your DNS:
            </p>
            <div className="bg-white p-3 rounded border border-blue-200 font-mono text-xs">
              <div className="grid grid-cols-3 gap-2 text-blue-900">
                <div>
                  <span className="font-semibold">Type:</span> TXT
                </div>
                <div>
                  <span className="font-semibold">Name:</span> _draftiq-verify
                </div>
                <div>
                  <span className="font-semibold">Value:</span> {domains?.find(d => d?.verification_status === 'pending')?.verification_token}
                </div>
              </div>
            </div>
            <p className="text-xs text-blue-700 mt-2">
              DNS changes may take up to 48 hours to propagate. Click Verify once the record is added.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainVerification;